import React, { useState, useEffect, useMemo } from 'react';
import {
  X, MessageSquare, Users, Send, Clock, Loader2, AlertCircle, ChevronDown,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button }    from '../ui/button';
import { Input }     from '../ui/input';
import { Textarea }  from '../ui/textarea';
import { toast }     from 'sonner';
import { useAudiences } from '../../context/AudienceContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ── SMS character counter helper ──────────────────────────────────────────────

function getSmsStats(text) {
  const len      = (text || '').length;
  const segSize  = len <= 160 ? 160 : 153;   // GSM-7
  const segments = len === 0 ? 1 : Math.ceil(len / segSize);
  const remaining = segments * segSize - len;
  return { len, segments, remaining };
}

// ── Auth helper ───────────────────────────────────────────────────────────────

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Not authenticated');
  return auth.currentUser.getIdToken();
}

// ── CampaignComposerModal ─────────────────────────────────────────────────────

/**
 * CampaignComposerModal
 *
 * Props:
 *   isOpen  — boolean
 *   onClose — fn()
 */
const CampaignComposerModal = ({ isOpen, onClose }) => {
  const { audiences } = useAudiences();

  const [name,        setName]        = useState('');
  const [audienceId,  setAudienceId]  = useState('');
  const [message,     setMessage]     = useState('');
  const [sendMode,    setSendMode]    = useState('now');    // 'now' | 'later'
  const [scheduledAt, setScheduledAt] = useState('');
  const [sending,     setSending]     = useState(false);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setAudienceId(audiences[0]?.id || '');
    setMessage('');
    setSendMode('now');
    setScheduledAt('');
  }, [isOpen, audiences]);

  const selectedAudience = useMemo(
    () => audiences.find((a) => a.id === audienceId) || null,
    [audiences, audienceId],
  );

  const smsStats = useMemo(() => getSmsStats(message), [message]);

  const canSend = name.trim() && audienceId && message.trim() &&
    (sendMode === 'now' || (sendMode === 'later' && scheduledAt));

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const token = await getAuthToken();
      const body  = {
        name:        name.trim(),
        audienceId,
        messageBody: message.trim(),
        sendNow:     sendMode === 'now',
        scheduledAt: sendMode === 'later' ? scheduledAt : null,
      };
      const res = await axios.post(`${API}/api/campaigns`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { status, sendableCount, skippedOptOut, noPhone } = res.data;

      if (status === 'sending') {
        toast.success(
          `Campaign started — sending to ${sendableCount} contacts` +
          (skippedOptOut > 0 ? ` (${skippedOptOut} opted out excluded)` : '') +
          (noPhone > 0       ? `, ${noPhone} had no phone` : ''),
        );
      } else if (status === 'scheduled') {
        toast.success(`Campaign scheduled for ${new Date(scheduledAt).toLocaleString()}`);
      } else {
        toast.success('Campaign queued');
      }

      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send campaign';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">

        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-500" />
            New SMS Campaign
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-0.5 font-normal">
            Send a targeted SMS to a saved audience segment
          </p>
        </DialogHeader>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto max-h-[70vh]">

          {/* Campaign Name */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Campaign Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Spring Promo — Lapsed Clients"
              data-testid="campaign-name-input"
              className="h-10"
            />
          </div>

          {/* Audience Selector */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Target Audience *
            </label>
            {audiences.length === 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                No audiences yet — create one first in the Audiences section.
              </div>
            ) : (
              <div className="relative">
                <select
                  value={audienceId}
                  onChange={(e) => setAudienceId(e.target.value)}
                  data-testid="campaign-audience-select"
                  className="w-full h-10 px-3 pr-8 border border-gray-200 rounded-lg text-sm bg-white
                    focus:outline-none focus:ring-2 focus:ring-orange-400 appearance-none"
                >
                  <option value="">Select an audience…</option>
                  {audiences.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} (~{(a.estimatedSize ?? 0).toLocaleString()} contacts)
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}

            {/* Selected audience info */}
            {selectedAudience && (
              <div className="flex items-center gap-2 mt-1">
                <Users className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-xs text-gray-500">
                  ~{(selectedAudience.estimatedSize ?? 0).toLocaleString()} estimated contacts
                  {' · '}Opted-out contacts will be excluded automatically
                </span>
              </div>
            )}
          </div>

          {/* Message Body */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Message *
              </label>
              {/* Character counter */}
              <span
                className={`text-xs font-medium ${
                  smsStats.segments > 1 ? 'text-amber-600' : 'text-gray-400'
                }`}
                data-testid="sms-char-counter"
              >
                {smsStats.len} chars &middot; {smsStats.segments} SMS segment{smsStats.segments !== 1 ? 's' : ''}
                {smsStats.segments === 1 && ` · ${smsStats.remaining} left`}
              </span>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi {name}, just wanted to reach out…"
              data-testid="campaign-message-input"
              rows={4}
              className="resize-none text-sm"
            />
            <p className="text-xs text-gray-400">
              Tip: Use personalization tokens like <code className="bg-gray-100 px-1 rounded">{'{'}name{'}'}</code>
            </p>
          </div>

          {/* Send Mode */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Send Timing
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSendMode('now')}
                data-testid="send-now-btn"
                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                  sendMode === 'now'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Send className={`w-4 h-4 shrink-0 ${sendMode === 'now' ? 'text-orange-500' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Send Now</p>
                  <p className="text-xs text-gray-500">Sends immediately</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSendMode('later')}
                data-testid="schedule-later-btn"
                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                  sendMode === 'later'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Clock className={`w-4 h-4 shrink-0 ${sendMode === 'later' ? 'text-orange-500' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Schedule</p>
                  <p className="text-xs text-gray-500">Pick date & time</p>
                </div>
              </button>
            </div>

            {sendMode === 'later' && (
              <Input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="schedule-datetime-input"
                className="h-10 text-sm"
                min={new Date().toISOString().slice(0, 16)}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t bg-white px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-400 hidden sm:block">
            Opt-out contacts are always excluded · Rate-limited delivery
          </p>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !canSend}
              data-testid="send-campaign-btn"
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              ) : sendMode === 'now' ? (
                <><Send className="w-4 h-4 mr-2" />Send Campaign</>
              ) : (
                <><Clock className="w-4 h-4 mr-2" />Schedule Campaign</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignComposerModal;
